import React, { useState, useEffect } from "react";
import { Patient } from "../../PatientCard";
import LabRequisitionService, {
  CreateLabRequisitionData,
  MedicalExamination,
} from "../../../../services/labRequisitionService";

interface LabRequisitionModalProps {
  patient: Patient;
  isOpen: boolean;
  onClose: () => void;
  onRequisitionCreated?: (requisitionId: number) => void;
  appointmentId?: number;
}

interface SelectedExamination extends MedicalExamination {
  special_instructions: string;
  is_urgent: boolean;
  estimated_cost: number;
}

const LabRequisitionModal: React.FC<LabRequisitionModalProps> = ({
  patient,
  isOpen,
  onClose,
  onRequisitionCreated,
  appointmentId,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<MedicalExamination[]>([]);
  const [selectedExaminations, setSelectedExaminations] = useState<
    SelectedExamination[]
  >([]);
  const [clinicalIndication, setClinicalIndication] = useState("");
  const [patientInstructions, setPatientInstructions] = useState("");
  const [externalLabName, setExternalLabName] = useState("");
  const [priority, setPriority] = useState<"routine" | "urgent" | "stat">(
    "routine"
  );
  const [manualEntry, setManualEntry] = useState("");
  const [manualEntryError, setManualEntryError] = useState("");
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [multipleSimilarTests, setMultipleSimilarTests] = useState<{
    originalSearch: string;
    found: Array<{ name: string; similar: MedicalExamination[] }>;
    notFound: Array<{ name: string }>;
  } | null>(null);

  const [bulkAddTests, setBulkAddTests] = useState<
    Array<{
      name: string;
      specimen_type: string;
      cost: number;
    }>
  >([]);

  const [showBulkAddForm, setShowBulkAddForm] = useState(false);
  const [showQuickAddForm, setShowQuickAddForm] = useState(false);
  const [quickAddData, setQuickAddData] = useState<{
    name: string;
    specimen_type: string;
    cost: number;
  }>({
    name: "",
    specimen_type: "Blood",
    cost: 0,
  });
  const [isQuickAdding, setIsQuickAdding] = useState(false);

  // Add specimen type options constant (after the existing state)
  const specimenTypeOptions = [
    "Blood",
    "Urine",
    "Stool",
    "Saliva",
    "Tissue",
    "Swab",
    "Various",
    "Other",
  ];
  // Search examinations with debounce
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        setSearchLoading(true);
        try {
          const results = await LabRequisitionService.searchExaminations(
            searchQuery
          );
          setSearchResults(results);
        } catch (error) {
          console.error("Examination search failed:", error);
          setSearchResults([]);
        } finally {
          setSearchLoading(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Clear state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSearchResults([]);
      setSelectedExaminations([]);
      setClinicalIndication("");
      setPatientInstructions("");
      setExternalLabName("");
      setPriority("routine");
      setNotes("");
      setErrors({});
    }
  }, [isOpen]);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);
  const addExamination = (examination: MedicalExamination) => {
    if (selectedExaminations.some((item) => item.id === examination.id)) {
      setErrors({
        examination: "This examination is already added to the requisition",
      });
      return;
    }

    const newItem: SelectedExamination = {
      ...examination,
      special_instructions: "",
      is_urgent: false,
      estimated_cost: examination.cost || 0,
    };

    setSelectedExaminations((prev) => [...prev, newItem]);
    setSearchQuery("");
    setSearchResults([]);
    setErrors({});
  };
  const addManualEntries = async () => {
    if (!manualEntry.trim()) {
      setManualEntryError("Please enter at least one test name");
      return;
    }

    const entries = manualEntry
      .split(/[\n,]+/)
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);

    if (entries.length === 0) {
      setManualEntryError("No valid test names found");
      return;
    }

    // Process each entry
    const processResults: {
      name: string;
      exists: boolean;
      similar: MedicalExamination[];
    }[] = [];

    for (const testName of entries) {
      // Skip if already in requisition
      if (
        selectedExaminations.some(
          (item) => item.name.toLowerCase() === testName.toLowerCase()
        )
      ) {
        continue;
      }

      // Check for similar tests in database
      const similarTests = await LabRequisitionService.searchExaminations(
        testName
      );

      processResults.push({
        name: testName,
        exists: similarTests.length > 0,
        similar: similarTests,
      });
    }

    const foundInDatabase = processResults.filter((r) => r.exists);
    const notFoundInDatabase = processResults.filter((r) => !r.exists);

    if (foundInDatabase.length > 0 || notFoundInDatabase.length > 0) {
      setMultipleSimilarTests({
        originalSearch: entries[0],
        found: foundInDatabase,
        notFound: notFoundInDatabase,
      });
      return;
    }

    setManualEntry("");
  };
  const handleQuickAddTest = async () => {
    if (!quickAddData.name.trim()) {
      setErrors({ quickAdd: "Test name is required" });
      return;
    }

    setIsQuickAdding(true);
    try {
      // Create custom examination with negative ID
      const customExam: SelectedExamination = {
        id: -Math.floor(Date.now() + Math.random() * 1000),
        name: quickAddData.name.trim(),
        specimen_type: quickAddData.specimen_type || "Various",
        cost: quickAddData.cost || 0,
        is_active: true,
        special_instructions: "",
        is_urgent: false,
        estimated_cost: quickAddData.cost || 0,
      };

      // Add to selected examinations
      setSelectedExaminations((prev) => [...prev, customExam]);

      // Reset form and close
      setQuickAddData({
        name: "",
        specimen_type: "Blood",
        cost: 0,
      });
      setShowQuickAddForm(false);
      setErrors({});
      setManualEntry("");
    } catch (error: any) {
      setErrors({ quickAdd: error.message || "Failed to add test" });
    } finally {
      setIsQuickAdding(false);
    }
  };
  const updateExamination = (
    index: number,
    field: keyof SelectedExamination,
    value: any
  ) => {
    setSelectedExaminations((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeExamination = (index: number) => {
    setSelectedExaminations((prev) => prev.filter((_, i) => i !== index));
  };

  const calculateTotalCost = (): number => {
    return selectedExaminations.reduce(
      (total, exam) => total + exam.estimated_cost,
      0
    );
  };

  const validateRequisition = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (selectedExaminations.length === 0) {
      newErrors.examinations = "Please add at least one examination";
    }

    if (!clinicalIndication.trim()) {
      newErrors.clinicalIndication = "Clinical indication is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateRequisition()) return;

    setIsSaving(true);
    try {
      const requisitionData: CreateLabRequisitionData = {
        patient_id: patient.id,
        appointment_id: appointmentId,
        requisition_date: new Date().toISOString().split("T")[0],
        external_lab_name: externalLabName || undefined,
        clinical_indication: clinicalIndication,
        patient_instructions: patientInstructions || undefined,
        priority,
        notes: notes || undefined,
        examinations: selectedExaminations.map((exam) => ({
          examination_id: exam.id,
          custom_name: exam.id < 0 ? exam.name : undefined, // Add custom name for manual entries
          special_instructions: exam.special_instructions || undefined,
          is_urgent: exam.is_urgent,
          estimated_cost: exam.estimated_cost,
        })),
      };

      const requisition = await LabRequisitionService.createLabRequisition(
        requisitionData
      );

      if (onRequisitionCreated) {
        onRequisitionCreated(requisition.id!);
      }

      onClose();
    } catch (error: any) {
      setErrors({ submit: error.message || "Failed to save lab requisition" });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const buttonStyle = (
    variant: "primary" | "secondary" | "danger" = "primary"
  ): React.CSSProperties => {
    const variants = {
      primary: { background: "#3b82f6", color: "white" },
      secondary: { background: "#e5e7eb", color: "#374151" },
      danger: { background: "#ef4444", color: "white" },
    };

    return {
      padding: isMobile ? "8px 16px" : "10px 20px",
      borderRadius: "8px",
      border: "none",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
      ...variants[variant],
      textAlign: "center",
    };
  };
  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    zIndex: 1000,
    display: "flex",
    alignItems: isMobile ? "flex-start" : "center",
    justifyContent: "center",
    padding: isMobile ? "10px" : "20px",
    overflow: "auto",
  };
  const modalStyle: React.CSSProperties = {
    backgroundColor: "#ffffff",
    borderRadius: isMobile ? "12px" : "16px",
    width: "100%",
    maxWidth: isMobile ? "100%" : "1000px",
    maxHeight: isMobile ? "100%" : "90vh",
    overflow: "hidden",
    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
    display: "flex",
    flexDirection: "column",
    margin: isMobile ? "10px 0" : "auto",
  };

  const headerStyle: React.CSSProperties = {
    padding: isMobile ? "16px" : isTablet ? "18px" : "20px",
    borderBottom: "1px solid #e5e7eb",
    backgroundColor: "#f8fafc",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: isMobile ? "12px" : isTablet ? "14px" : "16px",
    flexShrink: 0,
  };

  const contentStyle: React.CSSProperties = {
    flex: 1,
    overflow: "auto",
    padding: isMobile ? "16px" : "24px",
  };

  const footerStyle: React.CSSProperties = {
    padding: "20px",
    borderTop: "1px solid #e5e7eb",
    backgroundColor: "#f8fafc",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? "16px" : "0",
    flexShrink: 0,
  };
  return (
    <>
      <div
        style={overlayStyle}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div style={modalStyle}>
          {/* Header */}
          <div style={headerStyle} className="nn">
            <div style={{ flex: 1 }}>
              <h2
                style={{
                  fontSize: isMobile ? "18px" : isTablet ? "19px" : "20px",
                  fontWeight: "600",
                  margin: "0 0 4px 0",
                  color: "#1f2937",
                }}
              >
                🧪 New Lab Requisition
              </h2>
              <p
                style={{
                  fontSize: isMobile ? "13px" : isTablet ? "13px" : "14px",
                  color: "#64748b",
                  margin: 0,
                }}
              >
                Order lab tests for{" "}
                {patient.full_name ||
                  `${patient.first_name} ${patient.last_name}`}
              </p>
            </div>
            <button
              onClick={onClose}
              style={{ ...buttonStyle("secondary"), padding: "6px 10px" }}
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div style={contentStyle}>
            {/* Patient Info */}
            <div
              style={{
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "20px",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : isTablet
                    ? "repeat(2, 1fr)"
                    : "repeat(4, 1fr)",
                  gap: "12px",
                  marginBottom: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "13px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <strong>Patient:</strong>{" "}
                  {patient.full_name ||
                    `${patient.first_name} ${patient.last_name}`}
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <strong>Age:</strong> {patient.age} years
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <strong>Gender:</strong> {patient.gender}
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <strong>Phone:</strong> {patient.phone}
                </div>
              </div>
              {patient.allergies && patient.allergies.length > 0 && (
                <div
                  style={{
                    marginTop: "12px",
                    padding: "8px 12px",
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "6px",
                    color: "#dc2626",
                    fontSize: "12px",
                  }}
                >
                  ⚠️ <strong>Allergies:</strong> {patient.allergies.join(", ")}
                </div>
              )}
            </div>

            {/* Clinical Indication */}
            <div className="form-group">
              <label className="form-label">Clinical Indication *</label>
              <textarea
                value={clinicalIndication}
                onChange={(e) => setClinicalIndication(e.target.value)}
                placeholder="Enter clinical indication for lab tests..."
                rows={3}
                className={`form-input textarea ${
                  errors.clinicalIndication ? "error" : ""
                }`}
              />
              {errors.clinicalIndication && (
                <div className="error-message">{errors.clinicalIndication}</div>
              )}
            </div>

            {/* Priority and External Lab */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select
                  value={priority}
                  onChange={(e) =>
                    setPriority(e.target.value as "routine" | "urgent" | "stat")
                  }
                  className="form-input"
                >
                  <option value="routine">📅 Routine</option>
                  <option value="urgent">⚡ Urgent</option>
                  <option value="stat">🚨 STAT</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">External Lab (Optional)</label>
                <input
                  type="text"
                  id="external_lab_name"
                  name="external_lab_name"
                  value={externalLabName}
                  onChange={(e) => setExternalLabName(e.target.value)}
                  placeholder="e.g., Quest Diagnostics"
                  className="form-input"
                />
              </div>
            </div>

            {/* Examination Search */}
            <div className="form-group">
              <label className="form-label">Search & Add Examinations</label>
              <div className="search-container">
                <input
                  type="text"
                  id="search_lab_tests"
                  name="search_lab_tests"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for lab tests..."
                  className="form-input"
                />
                {searchLoading && <div className="search-loading">🔄</div>}
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map((examination) => (
                    <div
                      key={examination.id}
                      onClick={() => addExamination(examination)}
                      className="search-result-item"
                    >
                      <div className="result-name">{examination.name}</div>
                      <div className="result-meta">
                        <span>
                          Specimen: {examination.specimen_type || "N/A"}
                        </span>
                        <span>
                          Cost:{" "}
                          {LabRequisitionService.formatCurrency(
                            examination.cost || 0
                          )}
                        </span>
                      </div>
                      {examination.preparation_instructions && (
                        <div className="result-prep">
                          Prep: {examination.preparation_instructions}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {errors.examination && (
                <div className="error-message">{errors.examination}</div>
              )}
              {errors.examinations && (
                <div className="error-message">{errors.examinations}</div>
              )}
            </div>
            {/* Manual Entry Section */}
            <div className="form-group">
              <label className="form-label">
                Or Manually Enter Test Names
                <span
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    fontWeight: "normal",
                    marginLeft: "8px",
                  }}
                >
                  (Enter one test per line or separate with commas)
                </span>
              </label>
              <textarea
                value={manualEntry}
                onChange={(e) => {
                  setManualEntry(e.target.value);
                  setManualEntryError("");
                }}
                placeholder="Example:CBC&#10;FBS, HbA1C&#10;"
                rows={4}
                className={`form-input textarea ${
                  manualEntryError ? "error" : ""
                }`}
                style={{ fontFamily: "monospace", fontSize: "13px" }}
              />
              {manualEntryError && (
                <div className="error-message">{manualEntryError}</div>
              )}
              <button
                type="button"
                onClick={async () => {
                  if (!manualEntry.trim()) {
                    setManualEntryError("Please enter at least one test name");
                    return;
                  }

                  const entries = manualEntry
                    .split(/[\n,]+/)
                    .map((entry) => entry.trim())
                    .filter((entry) => entry.length > 0);

                  if (entries.length === 0) {
                    setManualEntryError("No valid test names found");
                    return;
                  }

                  // Process each entry
                  const processResults: {
                    name: string;
                    exists: boolean;
                    similar: MedicalExamination[];
                  }[] = [];

                  for (const testName of entries) {
                    if (
                      selectedExaminations.some(
                        (item) =>
                          item.name.toLowerCase() === testName.toLowerCase()
                      )
                    ) {
                      continue;
                    }

                    const similarTests =
                      await LabRequisitionService.searchExaminations(testName);

                    processResults.push({
                      name: testName,
                      exists: similarTests.length > 0,
                      similar: similarTests,
                    });
                  }

                  const foundInDatabase = processResults.filter(
                    (r) => r.exists
                  );
                  const notFoundInDatabase = processResults.filter(
                    (r) => !r.exists
                  );

                  if (
                    foundInDatabase.length > 0 ||
                    notFoundInDatabase.length > 0
                  ) {
                    setMultipleSimilarTests({
                      originalSearch: entries[0],
                      found: foundInDatabase,
                      notFound: notFoundInDatabase,
                    });
                    return;
                  }

                  setManualEntry("");
                }}
                style={{
                  ...buttonStyle("secondary"),
                  marginTop: "8px",
                  width: isMobile ? "100%" : "auto",
                }}
              >
                Check & Add Test(s)
              </button>
              <div
                style={{
                  fontSize: "11px",
                  color: "#6b7280",
                  marginTop: "8px",
                  fontStyle: "italic",
                }}
              >
                💡 Tip: You can paste multiple test names separated by commas or
                new lines
              </div>
            </div>
            {/* Selected Examinations */}
            {selectedExaminations.length > 0 && (
              <div className="form-group">
                <h3 className="section-title">
                  <span>
                    Selected Examinations ({selectedExaminations.length})
                  </span>
                  <span className="total-cost">
                    Total:{" "}
                    {LabRequisitionService.formatCurrency(calculateTotalCost())}
                  </span>
                </h3>
                <div className="selected-examinations">
                  {selectedExaminations.map((exam, index) => (
                    <div key={index} className="examination-card">
                      <div className="examination-header">
                        <div className="examination-info">
                          <div className="examination-name">{exam.name}</div>
                          <div className="examination-details">
                            Specimen: {exam.specimen_type || "N/A"} • Cost:{" "}
                            {LabRequisitionService.formatCurrency(
                              exam.estimated_cost
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => removeExamination(index)}
                          className="remove-button"
                          style={buttonStyle("danger")}
                        >
                          Remove
                        </button>
                      </div>

                      <div className="examination-controls">
                        <div className="control-group">
                          <label className="control-label">
                            Special Instructions
                          </label>
                          <input
                            type="text"
                            id={`special_instructions_${index}`}
                            name={`special_instructions_${index}`}
                            value={exam.special_instructions}
                            onChange={(e) =>
                              updateExamination(
                                index,
                                "special_instructions",
                                e.target.value
                              )
                            }
                            placeholder="e.g., Fasting required"
                            className="form-input small"
                          />
                        </div>

                        <div className="control-group checkbox-group">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              id={`is_urgent_${index}`}
                              name={`is_urgent_${index}`}
                              checked={exam.is_urgent}
                              onChange={(e) =>
                                updateExamination(
                                  index,
                                  "is_urgent",
                                  e.target.checked
                                )
                              }
                            />

                            <span>🚨 Urgent</span>
                          </label>
                        </div>

                        <div className="control-group">
                          <label className="control-label">Cost</label>
                          <input
                            type="number"
                            id={`estimated_cost_${index}`}
                            name={`estimated_cost_${index}`}
                            value={exam.estimated_cost}
                            onChange={(e) =>
                              updateExamination(
                                index,
                                "estimated_cost",
                                parseFloat(e.target.value) || 0
                              )
                            }
                            min="0"
                            step="0.01"
                            className="form-input small"
                          />
                        </div>
                      </div>

                      {exam.preparation_instructions && (
                        <div className="preparation-note">
                          💡 <strong>Preparation:</strong>{" "}
                          {exam.preparation_instructions}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Patient Instructions */}
            <div className="form-group">
              <label className="form-label">Patient Instructions</label>
              <textarea
                value={patientInstructions}
                onChange={(e) => setPatientInstructions(e.target.value)}
                placeholder="Instructions for the patient (e.g., fasting requirements, medication holds)..."
                rows={3}
                className="form-input textarea"
              />
            </div>

            {/* Notes */}
            <div className="form-group">
              <label className="form-label">Additional Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional notes or comments..."
                rows={2}
                className="form-input textarea"
              />
            </div>

            {errors.submit && (
              <div className="submit-error">{errors.submit}</div>
            )}

            {/* Footer */}
            <div style={footerStyle}>
              <div
                style={{
                  fontSize: isMobile ? "13px" : "13px",
                  color: "#6b7280",
                  textAlign: "center",
                }}
              >
                {selectedExaminations.length > 0 && (
                  <>
                    {selectedExaminations.length} examination(s) • Total Cost:{" "}
                    {LabRequisitionService.formatCurrency(calculateTotalCost())}
                  </>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  width: isMobile ? "100%" : "auto",
                }}
              >
                <button
                  onClick={onClose}
                  style={{
                    ...buttonStyle("secondary"),
                    flex: isMobile ? "1" : "none",
                  }}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  style={{
                    ...buttonStyle("primary"),
                    opacity: isSaving ? 0.7 : 1,
                    cursor: isSaving ? "not-allowed" : "pointer",
                    flex: isMobile ? "1" : "none",
                  }}
                  disabled={isSaving}
                >
                  {isSaving ? "💾 Saving..." : "💾 Save Requisition"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Multiple Similar Tests Modal */}
      {multipleSimilarTests && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "600px",
              width: "100%",
              maxHeight: "80vh",
              overflow: "auto",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "16px",
                color: "#1f2937",
              }}
            >
              Found {multipleSimilarTests.found.length} Test(s) in Database
            </h3>

            {/* Tests found in database */}
            {multipleSimilarTests.found.map((result, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: "16px",
                  padding: "12px",
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    color: "#166534",
                  }}
                >
                  Searching for: "{result.name}"
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    marginBottom: "8px",
                    color: "#6b7280",
                  }}
                >
                  Found {result.similar.length} match(es):
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {result.similar.map((exam) => (
                    <div
                      key={exam.id}
                      onClick={() => {
                        addExamination(exam);
                        setMultipleSimilarTests((prev) => {
                          if (!prev) return null;
                          const newFound = prev.found.filter(
                            (_, i) => i !== idx
                          );
                          if (
                            newFound.length === 0 &&
                            prev.notFound.length === 0
                          ) {
                            return null;
                          }
                          return { ...prev, found: newFound };
                        });
                      }}
                      style={{
                        padding: "10px",
                        background: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "6px",
                        cursor: "pointer",
                        transition: "background-color 0.2s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f8fafc")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = "white")
                      }
                    >
                      <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                        {exam.name}
                      </div>
                      <div style={{ fontSize: "11px", color: "#64748b" }}>
                        Specimen: {exam.specimen_type} • Cost:{" "}
                        {LabRequisitionService.formatCurrency(exam.cost || 0)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Tests not found */}
            {multipleSimilarTests.notFound.length > 0 && (
              <div
                style={{
                  padding: "12px",
                  background: "#fffbeb",
                  border: "1px solid #fcd34d",
                  borderRadius: "8px",
                  marginTop: "16px",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    color: "#92400e",
                  }}
                >
                  Not found in database:
                </div>
                <ul
                  style={{ margin: "0", paddingLeft: "20px", color: "#78350f" }}
                >
                  {multipleSimilarTests.notFound.map((result, idx) => (
                    <li key={idx}>{result.name}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Buttons */}
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "20px",
              }}
            >
              <button
                onClick={() => {
                  setMultipleSimilarTests(null);
                  setManualEntry("");
                }}
                style={buttonStyle("secondary")}
              >
                Cancel
              </button>

              {multipleSimilarTests.notFound.length > 0 && (
                <button
                  onClick={() => {
                    setBulkAddTests(
                      multipleSimilarTests.notFound.map((r) => ({
                        name: r.name,
                        specimen_type: "Blood",
                        cost: 0,
                      }))
                    );
                    setShowBulkAddForm(true);
                    setMultipleSimilarTests(null);
                    setManualEntry("");
                  }}
                  style={buttonStyle("primary")}
                >
                  Add as Custom ({multipleSimilarTests.notFound.length})
                </button>
              )}

              {multipleSimilarTests.found.length > 0 && (
                <button
                  onClick={() => {
                    setQuickAddData({
                      name: multipleSimilarTests.originalSearch,
                      specimen_type: "Blood",
                      cost: 0,
                    });
                    setShowQuickAddForm(true);
                    setMultipleSimilarTests(null);
                    setManualEntry("");
                  }}
                  style={{ ...buttonStyle("primary"), background: "#10b981" }}
                >
                  Add New "{multipleSimilarTests.originalSearch}"
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Form */}
      {showQuickAddForm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 2100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "500px",
              width: "100%",
              maxHeight: "90vh",
              overflow: "auto",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "16px",
                color: "#1f2937",
              }}
            >
              Add Custom Test
            </h3>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Test Name *
              </label>
              <input
                type="text"
                value={quickAddData.name}
                onChange={(e) =>
                  setQuickAddData({ ...quickAddData, name: e.target.value })
                }
                placeholder="e.g., CBC"
                className="form-input"
                autoFocus
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Specimen Type
              </label>
              <select
                value={quickAddData.specimen_type}
                onChange={(e) =>
                  setQuickAddData({
                    ...quickAddData,
                    specimen_type: e.target.value,
                  })
                }
                className="form-input"
              >
                {specimenTypeOptions.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Estimated Cost
              </label>
              <input
                type="number"
                value={quickAddData.cost}
                onChange={(e) =>
                  setQuickAddData({
                    ...quickAddData,
                    cost: parseFloat(e.target.value) || 0,
                  })
                }
                min="0"
                step="0.01"
                placeholder="0.00"
                className="form-input"
              />
            </div>

            {errors.quickAdd && (
              <div
                style={{
                  color: "#ef4444",
                  fontSize: "12px",
                  marginBottom: "16px",
                  padding: "8px",
                  background: "#fef2f2",
                  borderRadius: "6px",
                }}
              >
                {errors.quickAdd}
              </div>
            )}

            <div
              style={{
                padding: "12px",
                background: "#eff6ff",
                border: "1px solid #dbeafe",
                borderRadius: "6px",
                fontSize: "12px",
                color: "#1e40af",
                marginBottom: "16px",
              }}
            >
              This will add a custom test to your requisition. It won't be saved
              to the database.
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => {
                  setShowQuickAddForm(false);
                  setQuickAddData({
                    name: "",
                    specimen_type: "Blood",
                    cost: 0,
                  });
                  setErrors({});
                  setManualEntry("");
                }}
                style={buttonStyle("secondary")}
                disabled={isQuickAdding}
              >
                Cancel
              </button>
              <button
                onClick={handleQuickAddTest}
                style={{
                  ...buttonStyle("primary"),
                  opacity: isQuickAdding ? 0.7 : 1,
                  cursor: isQuickAdding ? "not-allowed" : "pointer",
                }}
                disabled={isQuickAdding}
              >
                {isQuickAdding ? "Adding..." : "Add Custom Test"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Add Form */}
      {showBulkAddForm && bulkAddTests.length > 0 && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 2100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "600px",
              width: "100%",
              maxHeight: "80vh",
              overflow: "auto",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "16px",
                color: "#1f2937",
              }}
            >
              Add {bulkAddTests.length} Custom Test(s)
            </h3>

            {bulkAddTests.map((test, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: "20px",
                  padding: "16px",
                  background: "#f8fafc",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                }}
              >
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "12px",
                  }}
                >
                  {idx + 1}. {test.name}
                </h4>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "12px",
                  }}
                >
                  <div>
                    <label className="control-label">Specimen Type</label>
                    <select
                      value={test.specimen_type}
                      onChange={(e) => {
                        const updated = [...bulkAddTests];
                        updated[idx].specimen_type = e.target.value;
                        setBulkAddTests(updated);
                      }}
                      className="form-input small"
                    >
                      {specimenTypeOptions.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="control-label">Cost</label>
                    <input
                      type="number"
                      value={test.cost}
                      onChange={(e) => {
                        const updated = [...bulkAddTests];
                        updated[idx].cost = parseFloat(e.target.value) || 0;
                        setBulkAddTests(updated);
                      }}
                      min="0"
                      step="0.01"
                      className="form-input small"
                    />
                  </div>
                </div>
              </div>
            ))}

            {errors.quickAdd && (
              <div
                style={{
                  color: "#ef4444",
                  fontSize: "12px",
                  marginBottom: "16px",
                  padding: "8px",
                  background: "#fef2f2",
                  borderRadius: "6px",
                }}
              >
                {errors.quickAdd}
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "20px",
              }}
            >
              <button
                onClick={() => {
                  setShowBulkAddForm(false);
                  setBulkAddTests([]);
                  setManualEntry("");
                }}
                style={buttonStyle("secondary")}
                disabled={isQuickAdding}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setIsQuickAdding(true);
                  try {
                    bulkAddTests.forEach((testData) => {
                      const customExam: SelectedExamination = {
                        id: -Math.floor(Date.now() + Math.random() * 1000),
                        name: testData.name,
                        specimen_type: testData.specimen_type,
                        cost: testData.cost,
                        is_active: true,
                        special_instructions: "",
                        is_urgent: false,
                        estimated_cost: testData.cost,
                      };
                      setSelectedExaminations((prev) => [...prev, customExam]);
                    });

                    setShowBulkAddForm(false);
                    setBulkAddTests([]);
                    setManualEntry("");
                  } catch (error: any) {
                    setErrors({ quickAdd: error.message });
                  } finally {
                    setIsQuickAdding(false);
                  }
                }}
                style={{
                  ...buttonStyle("primary"),
                  opacity: isQuickAdding ? 0.7 : 1,
                  cursor: isQuickAdding ? "not-allowed" : "pointer",
                }}
                disabled={isQuickAdding}
              >
                {isQuickAdding
                  ? "Adding..."
                  : `Add All ${bulkAddTests.length} Custom Tests`}
              </button>
            </div>
          </div>
        </div>
      )}
      <style>
        {`
         input:focus, select:focus, textarea:focus {
         border-color: #3b82f6 !important;
         box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
       }
       
       button:hover:not(:disabled) {
         transform: translateY(-1px);
         box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
       }
       
       @media (max-width: 768px) {
         .mobile-stack {
           flex-direction: column !important;
         }
         
         .mobile-full-width {
           width: 100% !important;
         }
         
         .mobile-grid-single {
           grid-template-columns: 1fr !important;
         }
         
         .mobile-text-left {
           text-align: left !important;
         }
       }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.6);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
        }

        .modal-container {
          background-color: #ffffff;
          border-radius: 16px;
          width: 100%;
          max-width: 480px;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
          display: flex;
          flex-direction: column;
        }

        .modal-header {
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
          background-color: #f8fafc;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
        }

        .header-info {
          flex: 1;
        }

        .header-info h2 {
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 4px 0;
          color: #1f2937;
        }

        .header-info p {
          font-size: 13px;
          color: #64748b;
          margin: 0;
        }

        .close-button {
          padding: 8px 12px !important;
          font-size: 16px;
          flex-shrink: 0;
        }

        .modal-content {
          flex: 1;
          overflow: auto;
          padding: 20px;
        }

        .patient-info {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 20px;
        }

        .patient-details {
          display: grid;
          grid-template-columns: 1fr;
          gap: 8px;
          margin-bottom: 8px;
        }

        .detail-item {
          font-size: 13px;
          display: flex;
          justify-content: space-between;
        }

        .allergies-alert {
          margin-top: 12px;
          padding: 8px 12px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 6px;
          color: #dc2626;
          font-size: 12px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr;
          gap: 16px;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 8px;
          color: #374151;
        }

        .form-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s ease;
        }

        .form-input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-input.error {
          border-color: #ef4444;
        }

        .form-input.small {
          padding: 8px 12px;
          font-size: 12px;
        }

        .textarea {
          resize: vertical;
          min-height: 80px;
        }

        .error-message {
          color: #ef4444;
          font-size: 12px;
          margin-top: 4px;
        }

        .search-container {
          position: relative;
        }

        .search-loading {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
        }

        .search-results {
          margin-top: 8px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          max-height: 200px;
          overflow: auto;
        }

        .search-result-item {
          padding: 12px 16px;
          border-bottom: 1px solid #f3f4f6;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .search-result-item:hover {
          background-color: #f8fafc;
        }

        .search-result-item:last-child {
          border-bottom: none;
        }

        .result-name {
          font-weight: 500;
          margin-bottom: 4px;
          font-size: 14px;
        }

        .result-meta {
          font-size: 12px;
          color: #64748b;
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 8px;
        }

        .result-prep {
          font-size: 11px;
          color: #9ca3af;
          margin-top: 4px;
          font-style: italic;
        }

        .section-title {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 16px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .total-cost {
          font-size: 14px;
          color: #6b7280;
          font-weight: normal;
        }

        .selected-examinations {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .examination-card {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;
        }

        .examination-header {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 12px;
        }

        .examination-info {
          flex: 1;
        }

        .examination-name {
          font-weight: 500;
          margin-bottom: 4px;
          font-size: 14px;
        }

        .examination-details {
          font-size: 12px;
          color: #64748b;
        }

        .remove-button {
          padding: 4px 8px !important;
          font-size: 12px !important;
          align-self: flex-start;
        }

        .examination-controls {
          display: grid;
          grid-template-columns: 1fr;
          gap: 12px;
          align-items: end;
        }

        .control-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .control-label {
          font-size: 12px;
          font-weight: 500;
          color: #374151;
        }

        .checkbox-group {
          flex-direction: row;
          align-items: center;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
        }

        .checkbox-label input[type="checkbox"] {
          width: auto;
          margin: 0;
        }

        .preparation-note {
          margin-top: 12px;
          padding: 8px 12px;
          background: #eff6ff;
          border: 1px solid #dbeafe;
          border-radius: 6px;
          font-size: 12px;
          color: #1e40af;
        }

        .submit-error {
          color: #ef4444;
          font-size: 14px;
          margin-bottom: 16px;
          padding: 12px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 6px;
        }

        .modal-footer {
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding-top: 16px;
          border-top: 1px solid #e5e7eb;
        }

        .footer-info {
          font-size: 13px;
          color: #6b7280;
          text-align: center;
        }

        .footer-buttons {
          display: flex;
          gap: 12px;
          justify-content: stretch;
        }

        .footer-buttons button {
          flex: 1;
        }

        /* Tablet styles */
        @media (min-width: 640px) {
          .modal-overlay {
            padding: 20px;
          }

          .modal-container {
            max-width: 600px;
          }

          .header-info h2 {
            font-size: 20px;
          }

          .header-info p {
            font-size: 14px;
          }

          .modal-content {
            padding: 24px;
          }

          .patient-details {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }

          .detail-item {
            font-size: 14px;
          }

          .form-row {
            grid-template-columns: 1fr 1fr;
          }

          .section-title {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
          }

          .examination-header {
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
          }

          .examination-controls {
            grid-template-columns: 2fr 1fr 1fr;
          }

          .modal-footer {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
          }

          .footer-buttons {
            justify-content: flex-end;
          }

          .footer-buttons button {
            flex: none;
            min-width: 120px;
          }
        }

        /* Desktop styles */
        @media (min-width: 1024px) {
          .modal-container {
            max-width: 1000px;
          }

          .patient-details {
            grid-template-columns: repeat(4, 1fr);
          }

          .result-meta {
            flex-wrap: nowrap;
          }
        }

        button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      `}
      </style>
    </>
  );
};
export default LabRequisitionModal;
